/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTheme } from "@mui/material/styles";
import { millisecondsToSeconds } from "date-fns/esm/fp";
import it from "date-fns/locale/it";
import { t } from "i18next";
import { type MutableRefObject, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import type { Swiper as SwiperType } from "swiper";
import { switchLocaleImport } from "./components/Calendar/LocaleLogic";
import { CONTENT_TYPE, ENROLL_TYPE, LEVEL, NAVBAR_ITEMS, STATUS_LO } from "./consts";
import { usePostCommunityTracking } from "./queries/community";
import { useEnrollmentMutation } from "./queries/general";
import { useNavigate } from "./services";
import { useAuthStore, useComunityStore, useLanguageStore, useNavbarStore } from "./stores";
import type { ContentType, LearningObject, Level } from "./types";
import { addEscapeString } from "./utils/general";


export function useAccent({
  isExpired,
  isMandatory,
  isSuggestedAI,
  isSuggestedHR,
  isSuggestedManager,
  status
}:
                          Partial<
                            Pick<
                              LearningObject,
                              | "isExpired"
                              | "isSuggestedAI"
                              | "isSuggestedHR"
                              | "isSuggestedManager"
                              | "isMandatory"
                              | "status"
                            >
                          >
) {
  const theme = useTheme();

  return useMemo(() => (
    isExpired && isMandatory &&
    (status === "E" || status === "P")
      ? theme.customColors.backgroundWarning
      : status === STATUS_LO.C || isSuggestedAI || isSuggestedHR || isSuggestedManager
        ? theme.customColors.accentVariantB
        : isMandatory ? theme.customColors.backgroundWarning : theme.customColors.accentVariantA
  ), [
    isExpired,
    isMandatory,
    isSuggestedAI,
    isSuggestedHR,
    isSuggestedManager,
    status,
    theme.customColors.accentVariantA,
    theme.customColors.accentVariantB,
    theme.customColors.accentVariantC
  ]);
}

export function useContentsMap({
  booked = false,
  learningObjectTypology,
  status
}:
                               & Partial<
                                 Pick<
                                   LearningObject,
                                   | "learningObjectTypology"
                                   | "status"
                                 >
                                 & { booked: boolean }
                               >
) {
  const contentsMap = {
    ASYNC: {
      info: CONTENT_TYPE.ASYNC,
      label: getCtaLabel({ booked, status }),
      learningObjectTypology: `${useLevel(learningObjectTypology)}`
    },
    BLENDED: {
      info: CONTENT_TYPE.BLENDED,
      label: getCtaLabel({ booked, status }),
      learningObjectTypology: `${useLevel(learningObjectTypology)}`
    },
    SYNC: {
      info: CONTENT_TYPE.SYNC,
      label: booked ? "detail" : "register",
      learningObjectTypology: `${useLevel(learningObjectTypology)}`
    }
  } as const;
  return contentsMap;
}

type ButtonStatusProps =
  & Partial<
  Pick<
    LearningObject,
    | "ecm_specialization"
    | "ecmRegistration"
    | "learningObjectType"
    | "learningObjectTypology"
    | "percentageOfCompletion"
    | "enrollType"
  >
> & {
  isCard?: boolean
  isSurvey?: boolean
  isTest?: boolean
  madeAttempts?: number
}

export function useButtonStatusMap({
  ecm_specialization,
  ecmRegistration,
  enrollType,
  isCard = true,
  isSurvey = false,
  isTest = false,
  learningObjectType,
  learningObjectTypology,
  madeAttempts = 0,
  percentageOfCompletion = "0%"
}: ButtonStatusProps) {
  const buttonMap = ({
    C: {
      label: isCompletedText({
        isCard,
        isSurvey,
        isTest,
        learningObjectType,
        learningObjectTypology,
        percentageOfCompletion
      })
    }, //COMPLETED
    E: {
      label: isEnrolledText({
        ecm_specialization,
        ecmRegistration,
        isCard, isSurvey, isTest, learningObjectType, learningObjectTypology, madeAttempts
      })
    }, //ENROLLED
    N: {
      label: notIsEnrolledTex({
        ecm_specialization,
        ecmRegistration,
        enrollType,
        isCard,
        isSurvey,
        isTest,
        learningObjectType,
        learningObjectTypology
      })
    }, // NOT ENROLLED
    P: {
      label: isProgressText({
        isSurvey: isSurvey,
        isTest: isTest,
        learningObjectTypology: learningObjectTypology
      })
    }, //PROGRESS
    R: {
      label: isCard ? "details" : "register_request"
    }, //REFUSED
    T: {
      label: notTpIsEnrolledTex({
        isCard,
        isSurvey,
        enrollType,
        isTest,
        learningObjectType,
        learningObjectTypology
      })
    },
    W: {
      label: isCard ? "details" : "validation_text"
    } //WAITING APPROVATION
  } as const);

  return buttonMap;
}

const isProgressText = ({
  isSurvey,
  isTest,
  learningObjectTypology
}: ButtonStatusProps) => {
  if (isTest || isSurvey) {
    return "complete";
  } else if (learningObjectTypology === LEVEL.DA_FINAL_BALANCE) {
    return "balance_view";
  } else if (learningObjectTypology === LEVEL.DA_GOALS) {
    return "goals_view";
  } else if (learningObjectTypology === LEVEL.DA_MATERIAL) {
    return "material_view";
  } else if (learningObjectTypology === LEVEL.DA_MEETING) {
    return "details";
  } else {
    return "resume";
  }
};
const notIsEnrolledTex = ({
  ecmRegistration,
  ecm_specialization,
  enrollType,
  isCard = false,
  isSurvey,
  isTest,
  learningObjectType,
  learningObjectTypology
}: ButtonStatusProps) => {
  const digital =
    learningObjectType === CONTENT_TYPE.ASYNC
    || learningObjectType === CONTENT_TYPE.BLENDED;

  if (isSurvey || isTest) {
    return "complete";
  }
  if (ecm_specialization) {
    if (!ecmRegistration) {
      if (isCard) {
        return "details";
      } else {
        return "register";
      }
    } else {
      return "start";
    }

  }
  //label LO NotEnrolled Card Case:
  if (isCard) {
    // digital LO or Blended (a Blended type could include a digital one)
    if (digital && enrollType === ENROLL_TYPE.AUTO_ENROLL && (
      learningObjectTypology !== LEVEL.DA_GOALS
      && learningObjectTypology !== LEVEL.DA_MEETING
      && learningObjectTypology !== LEVEL.DA_FINAL_BALANCE
      && learningObjectTypology !== LEVEL.DA_MATERIAL
    )) {
      return "start";
    } else {
      return "details";
    }
  } else if (learningObjectType === CONTENT_TYPE.SYNC) {
    if (
      learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
      || learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
    ) {
      if (enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL) {
        return "register_request";
      } else {
        return "editions_show";
      }
    } else if (learningObjectTypology === LEVEL.COURSE) {
      if (enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL) {
        return "register_request";
      } else {
        return "activities_show";
      }
    } else if (learningObjectTypology === LEVEL.DA_MEETING) {
      return "details";
    } else {
      return "modules_show";
    }
  } else if (digital && enrollType === ENROLL_TYPE.AUTO_ENROLL) {
    return "start";
  } else if (learningObjectTypology === LEVEL.DA_FINAL_BALANCE) {
    return "balance_view";
  } else if (learningObjectTypology === LEVEL.DA_GOALS) {
    return "goals_view";
  } else if (learningObjectTypology === LEVEL.DA_MATERIAL) {
    return "material_view";
  } else if (learningObjectTypology === LEVEL.DA_MEETING) {
    return "details";
  } else {
    return "register_request";
  }
};

const notTpIsEnrolledTex = ({
  isCard,
  isSurvey,
  isTest,
  enrollType,
  learningObjectType,
  learningObjectTypology
}: ButtonStatusProps) => {

  if (isSurvey || isTest) {
    return "complete";
  } else if (isCard) {
    return "details";
  } else if (learningObjectType === CONTENT_TYPE.SYNC) {
    if (
      learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
      || learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
    ) {
      if (enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL) {
        return "register_request";
      } else {
        return "editions_show";
      }
    } else if (learningObjectTypology === LEVEL.COURSE) {
      if (enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL) {
        return "register_request";
      } else {
        return "activities_show";
      }
    } else {
      return "modules_show";
    }
  } else {
    return "register_request";
  }
};

const isEnrolledText = ({
  ecm_specialization,
  ecmRegistration,
  isCard,
  isSurvey,
  isTest,
  learningObjectType,
  learningObjectTypology,
  madeAttempts
}: ButtonStatusProps) => {
  //label LO Enrolled All contents LIVE
  if (isSurvey || isTest) {
    if (madeAttempts && isTest) {
      return "retry";
    } else {
      return "complete";
    }
  }
  if (ecm_specialization) {
    if (!ecmRegistration) {
      if (isCard) {
        return "details";
      } else {
        return "register";
      }
    } else {
      return "start";
    }

  } else if (learningObjectType === CONTENT_TYPE.SYNC) {
    if (isCard) {
      //All Cards case:
      return "details";
    } else if (learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS) {
      // HeroBanner case live event fissare cta action su path
      // http://localhost:5173/esplora/dettaglio/348/DA_PHYSICAL_CLASS
      return "editions_show";
    } else if (learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS) {
      return "join";
    } else if (learningObjectTypology === LEVEL.COURSE) {
      // HeroBanner case virtual
      return "activities_show";
    } else {
      return "modules_show";
    }
  } else {
    if (learningObjectTypology === LEVEL.DA_FINAL_BALANCE) {
      return "balance_view";
    } else if (learningObjectTypology === LEVEL.DA_GOALS) {
      return "goals_view";
    } else if (learningObjectTypology === LEVEL.DA_MATERIAL) {
      return "material_view";
    } else if (learningObjectTypology === LEVEL.DA_URL_RES) {
      return isCard ? "details" : "content_view";
    } else if (learningObjectTypology === LEVEL.DA_MEETING) {
      return "details";
    } else {
      return "start";
    }

  }
};

const isCompletedText = ({
  isCard,
  isSurvey,
  isTest,
  learningObjectType,
  learningObjectTypology,
  percentageOfCompletion
}: ButtonStatusProps) => {
  if (isSurvey || isTest) {
    return "completed";
  }
  //label LO Enrolled All contents LIVE
  else if (learningObjectType === CONTENT_TYPE.SYNC) {
    if (isCard) {
      //All Cards case:
      return "details";
    } else if (
      learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
      || learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
    ) {
      return "editions_show";
    } else if (learningObjectTypology === LEVEL.COURSE) {
      // HeroBanner case virtual
      return "activities_show";
    } else {
      return "modules_show";
    }
  } else {
    if (learningObjectTypology === LEVEL.DA_FINAL_BALANCE) {
      return "balance_view";
    } else if (learningObjectTypology === LEVEL.DA_GOALS) {
      return "goals_view";
    } else if (learningObjectTypology === LEVEL.DA_MATERIAL) {
      return "material_view";
    } else if (learningObjectTypology === LEVEL.DA_MEETING) {
      return "details";
    } else if (learningObjectTypology === LEVEL.DA_URL_RES) {
      return isCard ? "details" : "content_view";
    } else {
      return percentageOfCompletion === "100%" || percentageOfCompletion === "100.0" ? "restart" : "resume";
    }
  }
};

export function useIntersectionObserver({
  callback,
  container
}: {
  callback: () => void
  container: MutableRefObject<HTMLDivElement | null>
}) {
  const [intersectionObserver, setIntersectionObserver] = useState<IntersectionObserver | null>(null);

  useEffect(() => {
    if (container.current === null) return;
    if (intersectionObserver === null) {
      const newObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              callback();
            }
          });
        },
        {
          root: container.current
        }
      );

      setIntersectionObserver(newObserver);
    }

    return () => intersectionObserver?.disconnect();
  }, [callback, container, intersectionObserver]);

  return intersectionObserver;
}

export function useResumeLink({
  ecmRegistration,
  ecm_specialization,
  enrollType,
  id,
  labelMoreInfo = false,
  learningObjectType,
  learningObjectTypology,
  isForYou,
  status,
  courseId,
  rootId
}: Pick<
  LearningObject,
  | "ecm_specialization"
  | "ecmRegistration"
  | "enrollType"
  | "id"
  | "learningObjectType"
  | "learningObjectTypology"
  | "status"
> & {
  labelMoreInfo?: boolean
  isForYou: boolean
  courseId?: number | undefined
  rootId?: number | undefined
}) {
  const buttonStatusMap = useButtonStatusMap({
    ecm_specialization,
    ecmRegistration,
    enrollType,
    learningObjectType,
    learningObjectTypology
  });
  const buttonText = buttonStatusMap[status]?.label;

  const navigate = useNavigate();
  if (!learningObjectTypology) {
    return "";
    //throw new Error("learningObjectTypology is required to navigate to detail");
  }
  if (
    !labelMoreInfo && shouldGoToWatchByLabel(buttonText)
  ) {
    return navigate.getWatchLink({
      id,
      isForYou,
      learningObjectTypology,
      courseId,
      rootId
    });
  } else {
    return navigate.getDetailLink({
      id,
      isForYou,
      learningObjectTypology,
      courseId,
      rootId
    });
  }
}

export const useType = (
  learningObjectType: ContentType,
  learningObjectTypology?: Level
) => {
  if (learningObjectType === CONTENT_TYPE.ASYNC) {
    return t("digital");
  } else if (learningObjectType === CONTENT_TYPE.BLENDED) {
    return t("blended");
  } else if (learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS) {
    return t("live_classroom");
  } else if (learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS) {
    return t("virtual_classroom");
  } else {
    return t("live");
  }
};

export const useLevel = (learningObjectTypology?: Level) => {
  const { t } = useTranslation();

  switch (learningObjectTypology) {
  case LEVEL.COURSE :
    return t("course");
  case LEVEL.DA_EXTERNAL_RES:
    return t("learning_activity");
  case LEVEL.DA_SCORM:
    return t("learning_activity");
  case LEVEL.DA_XAPI:
    return t("learning_activity");
  case LEVEL.DA_PHYSICAL_CLASS:
    return t("learning_activity");
  case LEVEL.DA_VIRTUAL_CLASS:
    return t("learning_activity");
  case LEVEL.PATH :
    return t("package");
  default:
    return t("learning_activity");
  }
};

export function useOnboardingSteps() {
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const hasProfessionStep = corporateInfo?.onboarding_capability
    || corporateInfo?.onboarding_executive_job
    || corporateInfo?.onboarding_personal_job;

  if (hasProfessionStep) {
    return ["personal_data", "profession", "personalization", "summary"];
  } else {
    return ["personal_data", "personalization", "summary"];
  }
}

function getCtaLabel({
  booked,
  status
}:
                     & Partial<
                         Pick<
                           LearningObject,
                           | "status"
                         >
                       >
                       & {
                       booked?: boolean,
                     }
) {
  if (!booked) {
    return "subscribe";
  } else if (status && status === STATUS_LO.E) {
    return "begin";
  } else if (status && status === STATUS_LO.C) {
    return "restart";
  } else {
    return "resume";
  }
}

export function useIconStatus({
  isSuggestedAI,
  isSuggestedHR,
  isSuggestedManager,
  status
}: Partial<
                                Pick<
                                  LearningObject,
                                  | "status"
                                  | "isSuggestedAI"
                                  | "isSuggestedHR"
                                  | "isSuggestedManager"
                                >
                              >
) {


  const iconInfo = useMemo(() => (
    isSuggestedAI ? "AI" :
      isSuggestedHR ? "Icons_invito" :
        isSuggestedManager ? "manager_suggested" :
          status === "C" ? "completato" : "Icons_obbligatorio"
  ), [
    isSuggestedAI,
    isSuggestedHR,
    isSuggestedManager,
    status
  ]);
  return iconInfo;
}

export function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = useState("");

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const updateScrollDirection = () => {
      const scrollY = window.scrollY;
      const direction = scrollY > lastScrollY ? "down" : "up";
      if (direction !== scrollDirection && (scrollY - lastScrollY > 5 || scrollY - lastScrollY < -5)) {
        setScrollDirection(direction);
      }
      lastScrollY = scrollY > 0 ? scrollY : 0;
    };
    window.addEventListener("scroll", updateScrollDirection); // add event listener
    return () => {
      window.removeEventListener("scroll", updateScrollDirection); // clean up
    };
  }, [scrollDirection]);

  return scrollDirection;
}

export function useElementSize(ref: RefObject<HTMLDivElement>) {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [elementSize, setElementSize] = useState<{
    height?: number,
    width?: number,
  }>({ height: undefined, width: undefined });

  useEffect(() => {
    function handleResize() {
      setElementSize({
        height: ref.current?.getBoundingClientRect()?.height,
        width: ref.current?.getBoundingClientRect()?.width
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [ref]);

  return elementSize;
}

export function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState<{
    height: number,
    width: number,
  }>({ height: Infinity, width: Infinity });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        height: window.innerHeight,
        width: window.innerWidth
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

export const useContactsWidget = (hasInitativeFreshDesk: boolean, language: string | null) => {
  const theme = useTheme();
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const userInfo = useAuthStore(state => state.userInfo);
  const clearDivs = () => {
    const scriptDivs = document.querySelectorAll("#freshworks-container") as NodeListOf<HTMLDivElement>;
    const iframeDivs = document.querySelectorAll("#freshworks-frame") as NodeListOf<HTMLDivElement>;

    scriptDivs?.forEach(div => {
      if (div.parentNode && div.parentNode.contains(div)) {
        div.parentNode.removeChild(div);
      }
    });

    iframeDivs?.forEach(div => {
      if (div.parentNode && div.parentNode.contains(div)) {
        div.parentNode.removeChild(div);
      }
    });
  };


  useEffect(() => {
    const idWidgetFreshdesk = (smartConfiguration?.idWidgetFreshdesk ?? "60000004142");
    const scriptSrc = `https://widget.freshworks.com/widgets/${idWidgetFreshdesk}.js`;

    if (hasInitativeFreshDesk && !window.FreshworksWidget) {
      const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
      if (!existingScript) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          clearDivs();
          window.fwSettings = {
            widget_id: idWidgetFreshdesk,
            locale: language || "it"
          };
          const freshworksWidgetScript = document.createElement("script");
          freshworksWidgetScript.src = scriptSrc;
          freshworksWidgetScript.async = true;

          const observer = new MutationObserver((mutations, obs) => {
            const iframe = document.getElementById("freshworks-container");
            const targetContainer = document.getElementById("stickyElements");

            if (iframe && targetContainer) {
              targetContainer.appendChild(iframe); // move iframe into sticky trail buttons container
              obs.disconnect();
            }
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true
          });

          document.body.appendChild(freshworksWidgetScript);
        }, 500);
      }
    }

    const addAccessibility = () => {
      const iframe = document.querySelector("iframe#launcher-frame") as HTMLIFrameElement | null;
      if (iframe instanceof HTMLIFrameElement) {
        Object.assign(iframe.style, {
          transition: "all 0.1s linear",
          position: "sticky",
          top: "calc(100% - 6rem)",
          pointerEvents: "auto",
          height: "5rem",
          maxWidth: "8rem"
        });
      }
      if (iframe instanceof HTMLIFrameElement) {
        const iFrameDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
        const iFrameLauncherButton = iFrameDoc?.querySelector(".launcher-button");
        if (iFrameLauncherButton) {
          const launcherButton = (iFrameLauncherButton as HTMLElement);
          launcherButton.style.border = `1px solid ${theme.customColors.borderPrimaryCta}`;
          launcherButton.setAttribute("tabindex", "0");
          launcherButton.setAttribute("role", "button");
          launcherButton.setAttribute("aria-label", t("helpdesk_launcher"));

          launcherButton.addEventListener("keydown", function(e: KeyboardEvent) {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              window.FreshworksWidget("open");
            }
          });
        }
      }

      const widgetFrame = document.querySelector("iframe#widget-frame");
      if (widgetFrame instanceof HTMLIFrameElement) {
        const widgetFrameDoc = widgetFrame?.contentDocument || widgetFrame?.contentWindow?.document;
        setTimeout(() => {
          const inviaButton = widgetFrameDoc?.getElementById("form-button");
          if (inviaButton) {
            inviaButton.style.outline = "none";
          }
          const nameInput = widgetFrameDoc?.getElementById("name");
          if (nameInput) nameInput.setAttribute("type", "text");
        }, 1500);
      }
    };
    
    // con MutationObserver si va a controllare se ci sono state mutazioni nel dom
    // l'iframe SUPPORTO viene aggiunto al DOM in un secondo momento
    const observer = new MutationObserver(() => addAccessibility());
    observer.observe(document.body, { childList: true, subtree: true });
    addAccessibility();

    return () => {
      observer.disconnect();
    };
  }, [hasInitativeFreshDesk, smartConfiguration?.idWidgetFreshdesk]);

  useEffect(() => {
    if (window.FreshworksWidget && hasInitativeFreshDesk) {
      window.FreshworksWidget("locale", language || "it");
      window.FreshworksWidget("identify", "ticketForm", {
        name: `${addEscapeString(userInfo?.name ?? "")} ${addEscapeString(userInfo?.surname ?? "")}`,
        email: userInfo?.email ?? ""
      });
      window.FreshworksWidget("setConfig", "ticketForm", {
        backgroundColor: theme.customColors.backgroundPrimaryCta
      });
      window.FreshworksWidget("setLabels", {
        [language || "it"]: {
          "banner": t("helpdesk_banner"),
          "launcher": t("helpdesk_launcher"),
          "contact_form": {
            "title": t("helpdesk_contact_form"),
            "submit": t("helpdesk_contact_form_submit"),
            "confirmation": t("helpdesk_contact_form_confirmation")
          }
        }
      });
    }
  }, [language, userInfo?.email, userInfo?.name, userInfo?.surname, hasInitativeFreshDesk, theme, t]);
};

export const useIsForYou = () => {
  const item = useNavbarStore(state => state.item);
  const isForYou = useMemo(
    () => item === NAVBAR_ITEMS.FOR_YOU,
    [item]
  );
  return isForYou;
};

function useIsCommunitySection() {
  const { pathname } = useLocation();
  return pathname.split("/")[1] === "community";
}

export function useCommunityTrackPathname() {
  const comunityStoredTime = useComunityStore(state => state.time);
  const comunityStatus = useComunityStore(state => state.status);
  const setCommunityState = useComunityStore(state => state.setComunityState);
  const isCommunitySection = useIsCommunitySection();
  const postCommunityTracking = usePostCommunityTracking();

  useEffect(() => {
    // on pathname change~~
    // if it's community section
    if (isCommunitySection) {
      // if community store is not initialized
      if (comunityStatus !== "initialized" && !comunityStoredTime) {
        // initize community store and track
        const currentTime = millisecondsToSeconds(new Date().getTime());
        setCommunityState({ status: "initialized", time: currentTime });
        postCommunityTracking({ duration: 0, verb: "viewed" });
      }
      // if it's not community section and community store is initialized
    } else if (comunityStoredTime) {
      // track community
      const currentTime = millisecondsToSeconds(new Date().getTime());
      const timeUpdate = currentTime - comunityStoredTime;
      setCommunityState({ status: "escaped", time: 0 });
      postCommunityTracking({ duration: timeUpdate, verb: "experienced" });
    }
  }, [
    comunityStatus,
    comunityStoredTime,
    isCommunitySection,
    postCommunityTracking,
    setCommunityState
  ]);
}

export function useCommunityTrackVisibility() {
  const comunityStoredTime = useComunityStore(state => state.time);
  const isCommunitySection = useIsCommunitySection();
  const setCommunityState = useComunityStore(state => state.setComunityState);
  const postCommunityTracking = usePostCommunityTracking();

  useEffect(() => {
    // on dom visibility change~~
    const handleVisibilityChange = () => {
      // if it's community section
      if (isCommunitySection) {
        // if the user is leaving (switched tabs or left the page)
        if (document.hidden) {
          // update community store and track
          let timeUpdate = 0;
          if (comunityStoredTime) {
            const currentTime = millisecondsToSeconds(new Date().getTime());
            timeUpdate = currentTime - comunityStoredTime;
          }
          setCommunityState({ status: "escaped", time: 0 });
          postCommunityTracking({ duration: timeUpdate, verb: "experienced" });
          // if the user is coming back to the page
        } else {
          // update community store
          const currentTime = millisecondsToSeconds(new Date().getTime());
          setCommunityState({ status: "initialized", time: currentTime });
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    comunityStoredTime,
    isCommunitySection,
    postCommunityTracking,
    setCommunityState
  ]);
}

type ButtonActionType = {
  enabled: boolean,
  function?: () => void
}

export const usePlayResumeVideo = ({
  ecm_specialization,
  ecmRegistration,
  enrollType,
  isCard,
  learningObjectType,
  learningObjectTypology,
  id,
  labelMoreInfo,
  sectionUrl,
  status,
  setOpenRegistrationModal,
  triggerOpenTabs,
  linkVirtualClass,
  navigate,
  parentId,
  rootId,
  grandParentId,
  courseId,
  isExpired,
  setOpenTojModal,
  attributesIds
}: {
  ecm_specialization: any,
  ecmRegistration: any,
  enrollType: any,
  isCard: any,
  learningObjectType: any,
  learningObjectTypology: any,
  id: any,
  labelMoreInfo: any,
  sectionUrl: any,
  status: any,
  setOpenRegistrationModal: any,
  triggerOpenTabs: any,
  linkVirtualClass: any,
  navigate: any,
  parentId: any,
  rootId: any,
  grandParentId: any,
  courseId: any,
  isExpired: any,
  setOpenTojModal: any,
  attributesIds?:number[]
}) => {

  const [actionButton, setActionButton] = useState<ButtonActionType>({
    enabled: true,
    function: undefined
  });
  const [sendRequest, setSendRequest] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const typologyEnrollment = rootId ? LEVEL.PATH : courseId ? LEVEL.COURSE : learningObjectTypology;
  const isForYou = sectionUrl.includes("per-te");
  const resumeLink = useResumeLink({
    ecm_specialization,
    ecmRegistration,
    enrollType,
    id,
    isForYou,
    labelMoreInfo,
    learningObjectType,
    learningObjectTypology,
    status,
    courseId,
    rootId
  });

  const idEnrollment = grandParentId ?
    grandParentId : parentId ?
      parentId : rootId ?
        rootId : courseId ?
          courseId : id;
  const language = useLanguageStore(state => state.language)?.id;
  const resumeLinkOpenEsternal = learningObjectTypology === LEVEL.DA_LTI || learningObjectTypology === LEVEL.DA_LINKEDIN ||
  learningObjectTypology === LEVEL.DA_URL_RES ?
    `/esplora/scorm/${id}/${learningObjectTypology}${
      courseId || rootId || language
        ? `?${[
          courseId ? `courseId=${courseId}` : "",
          rootId ? `rootId=${rootId}` : "",
          language ? `lang=${language}` : ""
        ].filter(Boolean).join("&")}`
        : ""
    }` :
    undefined;

  const {
    mutate: addEnrollmentMutation,
    error: enrollmentMutationError,
    isError: enrollmentMutationIsError,
    isSuccess: enrollmentMutationHadSuccess,
    isLoading: enrollmentIsLoading
  } = useEnrollmentMutation({
    enrollType: enrollType,
    learningObjectId: idEnrollment,
    learningObjectTypology: typologyEnrollment,
    attributesIds:attributesIds||[]
  });


  const resumeContent = useCallback(() => {
    navigate(
      resumeLink
    );
  }, [navigate, resumeLink]);


  useMemo(() => {
    // case -1 : TOJ activity
    if ((status === "E" || status === "P" || status === "C") && setOpenTojModal
      && (learningObjectTypology === LEVEL.DA_FINAL_BALANCE
        || learningObjectTypology === LEVEL.DA_GOALS
        || learningObjectTypology === LEVEL.DA_MATERIAL
        || learningObjectTypology === LEVEL.DA_MEETING
      )

    ) {
      setActionButton(
        {
          enabled: false,
          function: () => {
            setOpenTojModal(true);
          }
        }
      );
    }
    // case 0 : ALL ecm LO enrolled and not registered
    else if (status === "E" && ecm_specialization && !ecmRegistration && setOpenRegistrationModal) {
      setActionButton(
        {
          enabled: false,
          function: () => {
            setOpenRegistrationModal(true);
          }
        }
      );

    } else if (
      // case 1 : LIVE  and VIRTUAL LO in HeroBanner
      learningObjectType === CONTENT_TYPE.SYNC
      && triggerOpenTabs
      && !labelMoreInfo
    ) {
      if (
        learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
        && status !== "T"
        && status !== "N"
      ) {
        // if enrolled ==>  VIRTUAL LO
        setActionButton({
          enabled: true,
          function: () => {
            // for  virtual LO  Enrolled in HeroBanner to open external link metting if is available:
            if (linkVirtualClass) {
              window.open(linkVirtualClass, "_blank", "noreferrer");
            } else {
              // TO DO:  UNNECESSARY TO USE RESUMECONTENT()
              resumeContent();
            }

          }
        });
      } else if (
        enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL
        && status === "N"
      ) {
        setActionButton(
          {
            enabled: false,
            function: () => {
              setSendRequest(true);
              addEnrollmentMutation();
              // setBannerVisible(true);
            }
          }
        );
      } else {
        // if  NOT  enrolled : VIRTUAL LO && LIVE LO, action ==> OPEN TABS EDITIONS
        setActionButton({
          enabled: false,
          function: () => {
            triggerOpenTabs();
          }
        });
      }
    } else if (
      // case 2 : OTHER LO not enrolled
      status === "N"
      && !labelMoreInfo
      && !isExpired
    ) {
      // if enrollment action possible
      if (
        enrollType === ENROLL_TYPE.AUTO_ENROLL || enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL
      ) {
        setActionButton(
          {
            enabled: false,
            function: () => {
              // requestedAutoenroll possible only in details page
              if (isCard && enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL) {
                resumeContent();
              } else {
                addEnrollmentMutation();
              }
            }
          }
        );

      } else if (!isCard) {
        // TOP_DOWN CASE in hero banner (open banner ect..), enrollment action IS NOT possible
        setActionButton(
          {
            enabled: false,
            function: () => {
              setSendRequest(true);
              setBannerVisible(true);
            }
          }
        );
      } else {
        // TOP_DOWN CASE in card(navigate to detail..), enrollment action IS NOT possible
        setActionButton({
          enabled: true,
          function: () => {
            resumeContent();
          }
        });
      }
    } else {
      // case 3 : OTHER LO enrolled
      setActionButton({
        enabled: true,
        function: () => {
          if (resumeLinkOpenEsternal && (
            (learningObjectTypology === LEVEL.DA_URL_RES && !isCard) ||
            (learningObjectTypology === LEVEL.DA_LINKEDIN && resumeLink.includes("guarda")) ||
            (learningObjectTypology === LEVEL.DA_LTI && resumeLink.includes("guarda"))
          )) {
            window.open(resumeLinkOpenEsternal, "_blank", "noreferrer");
          } else {
            resumeContent();
          }
        }
      });
    }

  }, [
    status,
    ecm_specialization,
    ecmRegistration,
    setOpenRegistrationModal,
    learningObjectType,
    triggerOpenTabs,
    labelMoreInfo,
    isExpired,
    learningObjectTypology,
    linkVirtualClass,
    resumeContent,
    enrollType,
    isCard,
    addEnrollmentMutation,
    resumeLinkOpenEsternal,
    resumeLink,
    setOpenTojModal
  ]);


  return {
    resumeLink,
    sendRequest,
    bannerVisible,
    enrollmentMutationHadSuccess,
    enrollmentIsLoading,
    enrollmentMutationError,
    resumeLinkOpenEsternal,
    enrollmentMutationIsError,
    setBannerVisible,
    actionButton,
    resumeContent,
    addEnrollmentMutation
  };
};

/**
 * Function to determine if the button should lead to watch page based on its text.
 * @param buttonText
 */
export const shouldGoToWatchByLabel = (buttonText: string) => {
  return (buttonText === "restart"
        || buttonText === "resume"
        || buttonText === "start");
};

interface FullscreenDocument extends Document {
    webkitFullscreenElement?: Element | null;
    mozFullScreenElement?: Element | null;
    msFullscreenElement?: Element | null;
}

export function useFullscreenStatus(): boolean {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      const doc = document as FullscreenDocument;
      const isFs =
                doc.fullscreenElement ||
                doc.webkitFullscreenElement ||
                doc.mozFullScreenElement ||
                doc.msFullscreenElement;

      setIsFullscreen(!!isFs);
    };

    document.addEventListener("fullscreenchange", handleChange);
    document.addEventListener("webkitfullscreenchange", handleChange);
    document.addEventListener("mozfullscreenchange", handleChange);
    document.addEventListener("MSFullscreenChange", handleChange);

    handleChange();

    return () => {
      document.removeEventListener("fullscreenchange", handleChange);
      document.removeEventListener("webkitfullscreenchange", handleChange);
      document.removeEventListener("mozfullscreenchange", handleChange);
      document.removeEventListener("MSFullscreenChange", handleChange);
    };
  }, []);

  return isFullscreen;
}

export const useIsLoading = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  return { isLoading, setIsLoading };
};

export const useSweepState = () => {
  const [autoSweep, setAutoSweep] = useState<boolean>(true);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const handleAutoSweep = () => setAutoSweep((autoplay) => {
    if (autoplay) swiperInstance?.autoplay.stop();
    if (!autoplay) swiperInstance?.autoplay.start();
    return !autoplay;
  });

  return {
    autoSweep,
    handleAutoSweep,
    setSwiperInstance,
    swiperInstance,
    activeIndex,
    setActiveIndex
  };
};

export const useFontSizeObserver = () => {
  //TODO we can optimize this hook returning only the size instead of the boolean 
  const [hideWidgets, setHideWidgets] = useState<boolean | null>(null);

  useEffect(() => {
    // element to observe font size changes
    const el = document.createElement("div");
    el.style.position = "absolute";
    el.style.visibility = "hidden";
    el.style.height = "1rem";
    document.body.appendChild(el);

    const observer = new ResizeObserver(() => {
      const newSize = parseFloat(getComputedStyle(document.documentElement).fontSize);

      // when the new font size is greater than 16px, it means the user has increased the font size
      // from the browser settings.
      // we hide the widgets to avoid layout issues.
      if (newSize > 16) {
        setHideWidgets(true);
        return;
      }
      setHideWidgets(false);
    });

    observer.observe(el);

    // remove observer on component unmount
    return () => {
      observer.disconnect();
      el.remove();
      setHideWidgets(false);
    };
  }, []);

  return { hideWidgets };
};

export function useDateFnsLocale({ localeCode }: { localeCode: string }) {
  const [locale, setLocale] = useState<Locale>(it);

  useEffect(() => {
    (async () => {
      try {
        const newLocale = await switchLocaleImport(localeCode);
        setLocale(newLocale.default);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("import date-fns locale:", err);
      }
    })();
  }, [localeCode]);

  return locale;
}

type IScroll = {
footerHeightRem?: number
}
export const useScrollAtBottom = ({ footerHeightRem = 50 }: IScroll) => {

  const [hasReachedBottom, sethasReachedBottom] = useState<boolean>(false);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  let footerHeightPx = footerHeightRem * 16; //1 rem = 16px

  useEffect(() => {
    const checkScrollability = () => {
      const scrollable = document.documentElement.scrollHeight > window.innerHeight;
      sethasReachedBottom(scrollable);
    };

    // Controlla al caricamento
    checkScrollability();
    const handleScroll = () => {
      setIsScrolling((window.scrollY > 0));
      //Altezza totale del contenuto della pagina
      const scrollHeight = document.documentElement.scrollHeight;

      // Pixel scrollati dal top della pagina
      const scrollTop = window.scrollY;

      // Altezza della finestra del browser (parte visibile)
      const clientHeight = window.innerHeight;
      
      // -50px tolleranza generale
      if (scrollTop + clientHeight >= scrollHeight - footerHeightPx) {
        sethasReachedBottom(true);
        return;
      }
      
      sethasReachedBottom(false);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return { hasReachedBottom, isScrolling };
};


export function useElementHeightRem() {
  const ref = useRef(null);
  const [heightRem, setHeightRem] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (ref.current) {
        //@ts-ignore
        const heightPx = ref.current.offsetHeight;
        
        // Otteniamo il font-size reale del root (fallback a 16 se non trovato)
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        
        const calculatedRem = heightPx / rootFontSize;
        setHeightRem(parseFloat(calculatedRem.toFixed(3)));
      }
    };

    updateHeight();

    window.addEventListener("resize", updateHeight);
    
    const resizeObserver = new ResizeObserver(() => updateHeight());
    if (ref.current) resizeObserver.observe(ref.current);

    return () => {
      window.removeEventListener("resize", updateHeight);
      resizeObserver.disconnect();
    };
  }, []);

  return [ref, heightRem];
}


export function useTimer(timer: number, startState = true) {
  const [isActive, setIsActive] = useState<boolean>(startState);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setIsActive(false);
    }, timer);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      setIsActive(true);
    }
  };

  useEffect(() => {
    startTimer();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    stopTimer,
    startTimer,
    isActive
  };
}
